// Complementary Products - Add to Cart Logic
// This file handles all add-to-cart interactions for complementary products

// Shopify's product_recommendation_conversions report attributes add-to-cart only after
// a product page visit with pr_* URL params. Load recommendation.url in a hidden iframe
// before AJAX add-to-cart so + button UX still counts in that funnel.
const RECOMMENDATION_TRACKING_TIMEOUT_MS = 4000;
const RECOMMENDATION_TRACKING_SETTLE_MS = 800;
const RECOMMENDATION_TRACKING_IFRAME_RETENTION_MS = 10000;

function resolveRecommendationTrackingUrl(item) {
	const raw = item?.dataset?.productUrl;
	if (!raw || raw === '#') return null;
	if (raw.indexOf('pr_') === -1) return null;
	if (raw.startsWith('http')) return raw;
	const root = window.Shopify?.routes?.root || '/';
	return raw.startsWith('/') ? raw : root + raw.replace(/^\//, '');
}

function registerRecommendationTracking(item) {
	return new Promise((resolve) => {
		const trackingUrl = resolveRecommendationTrackingUrl(item);
		if (!trackingUrl) {
			resolve();
			return;
		}

		const iframe = document.createElement('iframe');
		iframe.setAttribute('aria-hidden', 'true');
		iframe.setAttribute('tabindex', '-1');
		// Non-zero dimensions; some browsers deprioritize or abort 0×0 iframes.
		iframe.style.cssText =
			'position:fixed;width:1px;height:1px;left:-9999px;top:-9999px;border:0;opacity:0;pointer-events:none';

		let settled = false;
		let navigationStarted = false;

		const finish = () => {
			if (settled) return;
			settled = true;
			clearTimeout(timeoutId);
			resolve();
			// Resolve before removal so cart/add can proceed, but keep the iframe
			// mounted long enough that the document request is not aborted (canceled).
			setTimeout(() => iframe.remove(), RECOMMENDATION_TRACKING_IFRAME_RETENTION_MS);
		};

		const timeoutId = setTimeout(finish, RECOMMENDATION_TRACKING_TIMEOUT_MS);

		iframe.addEventListener('load', () => {
			// Ignore the initial about:blank load; only react after src is assigned.
			if (!navigationStarted) return;
			setTimeout(finish, RECOMMENDATION_TRACKING_SETTLE_MS);
		});

		iframe.addEventListener('error', () => {
			if (!navigationStarted) return;
			finish();
		});

		document.body.appendChild(iframe);
		navigationStarted = true;
		iframe.src = trackingUrl;
	});
}

// Global functions for inline onclick handlers (like deeplinks)
function complementaryProductClick(variantId, item) {
	console.log('[Complementary Products] onClick - Product clicked', { variantId, item });
	if (!variantId) {
		console.error('[Complementary Products] No variant ID provided');
		return;
	}
	
	const addButton = item.querySelector('.complementary-products-inline__add-button');
	complementaryAddToCart(variantId, addButton || item, item);
}

function complementaryExpandClick(button) {
	console.log('[Complementary Products] onClick - Expand button clicked', button);
	const item = button.closest('.complementary-products-inline__item');
	const variantsPanel = item.querySelector('.complementary-products-inline__variants');
	
	if (variantsPanel) {
		const isExpanded = variantsPanel.classList.contains('expanded');
		if (isExpanded) {
			variantsPanel.classList.remove('expanded');
		} else {
			variantsPanel.classList.add('expanded');
		}
		
		// Rotate plus icon
		const plusIcon = button.querySelector('.complementary-products-inline__plus-icon');
		if (plusIcon) {
			plusIcon.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(45deg)';
		}
	}
}

function complementaryAddToCartClick(button) {
	console.log('[Complementary Products] onClick - Add to cart button clicked', button);
	const item = button.closest('.complementary-products-inline__item');
	const variantId = item.dataset.variantId;
	console.log('[Complementary Products] Variant ID from item:', variantId);
	
	if (variantId) {
		complementaryAddToCart(variantId, button, item);
	} else {
		console.error('[Complementary Products] No variant ID found on item', item);
	}
}

async function complementaryAddToCart(variantId, button, item) {
	console.log('[Complementary Products] addToCart called', { variantId, button, item });
	if (!variantId) {
		console.error('[Complementary Products] No variant ID provided to addToCart');
		return;
	}

	// Get the item if not provided
	if (!item) {
		item = button.closest('.complementary-products-inline__item');
	}

	if (button && button.tagName === 'BUTTON') {
		button.disabled = true;
	}

	await registerRecommendationTracking(item);

	// Detect context: are we in a cart drawer or cart page?
	const isInCartDrawer = item && (item.closest('cart-drawer') || item.closest('.cart-drawer'));
	const isInCartPage = item && item.closest('.main-cart');
	const contextName = isInCartDrawer ? 'Cart Drawer' : (isInCartPage ? 'Cart Page' : 'Product Page');
	console.log('[Complementary Products] Context:', contextName);

	// Store original state for potential rollback
	let originalButtonState = null;
	let originalAccordionState = null;
	let originalPlusIconState = null;
	let mainAddButton = null;

	// In cart drawer or cart page: immediately add visual feedback (fade out/slide) and hide error messages
	if ((isInCartDrawer || isInCartPage) && item) {
		item.classList.add('complementary-products-inline__item--adding');
		
		// Hide any cart error messages that might show during the animation
		const cartErrors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
		if (cartErrors) {
			cartErrors.style.display = 'none';
		}
	}

	// Only do optimistic UI updates if NOT in cart drawer or cart page (those will full refresh)
	if (!isInCartDrawer && !isInCartPage) {
		// Update UI immediately (optimistic update)
		// If this was from the variant picker, close the accordion and update the main button
		if (button && button.classList && button.classList.contains('complementary-products-inline__add-to-cart-button') && item) {
		// Store original accordion state
		const variantsPanel = item.querySelector('.complementary-products-inline__variants');
		if (variantsPanel) {
			originalAccordionState = variantsPanel.classList.contains('expanded');
			variantsPanel.classList.remove('expanded');
		}
		
		// Store and reset plus icon rotation
		const expandButton = item.querySelector('.complementary-products-inline__add-button--expand');
		if (expandButton) {
			const plusIcon = expandButton.querySelector('.complementary-products-inline__plus-icon');
			if (plusIcon) {
				originalPlusIconState = plusIcon.style.transform;
				plusIcon.style.transform = 'rotate(0deg)';
			}
		}
		
		// Update the main add button to show checkmark
		mainAddButton = item.querySelector('.complementary-products-inline__add-button');
		if (mainAddButton) {
			originalButtonState = {
				classList: mainAddButton.classList.toString(),
				innerHTML: mainAddButton.innerHTML
			};
			mainAddButton.classList.add('added');
			mainAddButton.innerHTML = '<svg class="complementary-products-inline__check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
		}
	} else if (button && button.classList) {
		// Update button state for single variant or direct clicks
		if (button.classList.contains('complementary-products-inline__add-button')) {
			originalButtonState = {
				classList: button.classList.toString(),
				innerHTML: button.innerHTML
			};
			button.classList.add('added');
			button.innerHTML = '<svg class="complementary-products-inline__check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
		} else if (button.classList.contains('complementary-products-inline__item')) {
			const addButton = button.querySelector('.complementary-products-inline__add-button');
			if (addButton) {
				originalButtonState = {
					classList: addButton.classList.toString(),
					innerHTML: addButton.innerHTML
				};
				addButton.classList.add('added');
				addButton.innerHTML = '<svg class="complementary-products-inline__check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
			}
		}
		}
	}

	// Disable buttons during request (only if NOT in cart drawer/page - those will refresh entirely)
	if (!isInCartDrawer && !isInCartPage) {
		if (button && button.tagName === 'BUTTON') {
			button.disabled = true;
		} else if (button && button.classList && button.classList.contains('complementary-products-inline__item')) {
			const addButton = button.querySelector('.complementary-products-inline__add-button');
			if (addButton) {
				addButton.disabled = true;
			}
		}
		if (mainAddButton) {
			mainAddButton.disabled = true;
		}
	}

	const formData = new FormData();
	formData.append('id', variantId);
	formData.append('quantity', 1);

	fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
		method: 'POST',
		body: formData
	})
	.then(response => {
		if (!response.ok) {
			throw new Error('Network response was not ok');
		}
		return response.json();
	})
	.then(data => {
		// Shopify returns {status, description} on error, or cart data on success
		if (data.status) {
			// Error response - revert UI changes
			console.error('[Complementary Products] Error adding to cart:', data.description || 'Unknown error');
			
			if (originalButtonState && mainAddButton) {
				mainAddButton.classList.value = originalButtonState.classList;
				mainAddButton.innerHTML = originalButtonState.innerHTML;
				mainAddButton.disabled = false;
			} else if (originalButtonState && button) {
				if (button.classList.contains('complementary-products-inline__add-button')) {
					button.classList.value = originalButtonState.classList;
					button.innerHTML = originalButtonState.innerHTML;
					button.disabled = false;
				} else if (button.classList.contains('complementary-products-inline__item')) {
					const addButton = button.querySelector('.complementary-products-inline__add-button');
					if (addButton) {
						addButton.classList.value = originalButtonState.classList;
						addButton.innerHTML = originalButtonState.innerHTML;
						addButton.disabled = false;
					}
				}
			}
			
			// Revert accordion state
			if (originalAccordionState !== null && item) {
				const variantsPanel = item.querySelector('.complementary-products-inline__variants');
				if (variantsPanel) {
					if (originalAccordionState) {
						variantsPanel.classList.add('expanded');
					} else {
						variantsPanel.classList.remove('expanded');
					}
				}
			}
			
			// Revert plus icon rotation
			if (originalPlusIconState !== null && item) {
				const expandButton = item.querySelector('.complementary-products-inline__add-button--expand');
				if (expandButton) {
					const plusIcon = expandButton.querySelector('.complementary-products-inline__plus-icon');
					if (plusIcon) {
						plusIcon.style.transform = originalPlusIconState;
					}
				}
			}
			
			if (button && button.tagName === 'BUTTON') {
				button.disabled = false;
			}
		} else {
			// Success - handle based on context
			console.log('[Complementary Products] Successfully added to cart');
			
			if (isInCartDrawer) {
				// In cart drawer: full refresh to show new item and update recommendations
				console.log('[Complementary Products] In cart drawer - triggering full cart drawer refresh');
				fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile' : '/?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile')
					.then(response => response.json())
					.then(sections => {
						// Update cart drawer inner content directly
						const drawerInner = document.querySelector('.drawer__inner');
						if (drawerInner && sections['cart-drawer']) {
							const parser = new DOMParser();
							const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
							const newDrawerInner = doc.querySelector('.drawer__inner');
							if (newDrawerInner) {
								drawerInner.innerHTML = newDrawerInner.innerHTML;
								
								// Clear any error messages that might have persisted
								const errorTexts = drawerInner.querySelectorAll('.cart-item__error-text');
								errorTexts.forEach(errorText => {
									errorText.innerHTML = '';
								});
								
								// Hide and clear cart errors after successful refresh
								const cartErrors = document.getElementById('CartDrawer-CartErrors');
								if (cartErrors) {
									cartErrors.style.display = 'none';
									cartErrors.innerHTML = '';
								}
								
								// Re-initialize complementary products after DOM update
								if (typeof window.initCartComplementaryProducts === 'function') {
									window.initCartComplementaryProducts();
								}
							}
						}
						
						// Update cart icon bubbles
						if (sections['cart-icon-bubble']) {
							const cartIconBubble = document.getElementById('cart-icon-bubble');
							if (cartIconBubble) {
								cartIconBubble.innerHTML = sections['cart-icon-bubble'];
							}
						}
						if (sections['cart-icon-bubble-mobile']) {
							const cartIconBubbleMobile = document.getElementById('cart-icon-bubble-mobile');
							if (cartIconBubbleMobile) {
								cartIconBubbleMobile.innerHTML = sections['cart-icon-bubble-mobile'];
							}
						}
					})
					.catch(err => {
						console.error('[Complementary Products] Error refreshing cart drawer:', err);
					});
			} else if (isInCartPage) {
				// On cart page: refresh cart items section
				console.log('[Complementary Products] On cart page - refreshing cart items');
				const mainCartItems = document.getElementById('main-cart-items');
				if (mainCartItems && mainCartItems.dataset.id) {
					const sectionId = mainCartItems.dataset.id;
					fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`)
						.then(response => response.text())
						.then(html => {
							const parser = new DOMParser();
							const doc = parser.parseFromString(html, 'text/html');
							const newCartItems = doc.querySelector('.js-contents');
							const currentCartItems = document.querySelector('.js-contents');
							if (newCartItems && currentCartItems) {
								currentCartItems.innerHTML = newCartItems.innerHTML;
							}
							
							// Also update footer totals
							const mainCartFooter = document.getElementById('main-cart-footer');
							if (mainCartFooter && mainCartFooter.dataset.id) {
								const footerSectionId = mainCartFooter.dataset.id;
								fetch(`${window.Shopify.routes.root}?section_id=${footerSectionId}`)
									.then(response => response.text())
									.then(footerHtml => {
										const footerDoc = parser.parseFromString(footerHtml, 'text/html');
										const newFooterContent = footerDoc.querySelector('.js-contents-2');
										const currentFooterContent = document.querySelector('.js-contents-2');
										if (newFooterContent && currentFooterContent) {
											currentFooterContent.innerHTML = newFooterContent.innerHTML;
										}
										
										// Re-initialize complementary products
										if (typeof window.initCartComplementaryProducts === 'function') {
											window.initCartComplementaryProducts();
										}
									})
									.catch(err => console.error('[Complementary Products] Error refreshing cart footer:', err));
							}
						})
						.catch(err => console.error('[Complementary Products] Error refreshing cart items:', err));
				}
			} else {
				// On product page: keep optimistic UI (checkmark already showing), just update cart drawer in background
				console.log('[Complementary Products] On product page - updating cart drawer in background, keeping optimistic UI');
				fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile' : '/?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile')
					.then(response => response.json())
					.then(sections => {
						// Update cart drawer inner content directly (in background)
						const drawerInner = document.querySelector('.drawer__inner');
						if (drawerInner && sections['cart-drawer']) {
							const parser = new DOMParser();
							const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
							const newDrawerInner = doc.querySelector('.drawer__inner');
							if (newDrawerInner) {
								drawerInner.innerHTML = newDrawerInner.innerHTML;
								
								// Clear any error messages that might have persisted
								const errorTexts = drawerInner.querySelectorAll('.cart-item__error-text');
								errorTexts.forEach(errorText => {
									errorText.innerHTML = '';
								});
								
								// Hide and clear cart errors after successful refresh
								const cartErrors = document.getElementById('CartDrawer-CartErrors');
								if (cartErrors) {
									cartErrors.style.display = 'none';
									cartErrors.innerHTML = '';
								}
								
								// Re-initialize complementary products after DOM update
								if (typeof window.initCartComplementaryProducts === 'function') {
									window.initCartComplementaryProducts();
								}
							}
						}
						
						// Update cart icon bubbles
						if (sections['cart-icon-bubble']) {
							const cartIconBubble = document.getElementById('cart-icon-bubble');
							if (cartIconBubble) {
								cartIconBubble.innerHTML = sections['cart-icon-bubble'];
							}
						}
						if (sections['cart-icon-bubble-mobile']) {
							const cartIconBubbleMobile = document.getElementById('cart-icon-bubble-mobile');
							if (cartIconBubbleMobile) {
								cartIconBubbleMobile.innerHTML = sections['cart-icon-bubble-mobile'];
							}
						}
					})
					.catch(err => {
						console.error('[Complementary Products] Error refreshing cart:', err);
					});
				
				// On product page: buttons stay disabled with checkmark (optimistic UI stays)
				// User sees immediate feedback, cart updates in background
			}
		}
	})
	.catch(error => {
		console.error('[Complementary Products] Error adding to cart:', error);
		
		// Revert UI changes on error
		if (originalButtonState && mainAddButton) {
			mainAddButton.classList.value = originalButtonState.classList;
			mainAddButton.innerHTML = originalButtonState.innerHTML;
			mainAddButton.disabled = false;
		} else if (originalButtonState && button) {
			if (button.classList.contains('complementary-products-inline__add-button')) {
				button.classList.value = originalButtonState.classList;
				button.innerHTML = originalButtonState.innerHTML;
				button.disabled = false;
			} else if (button.classList.contains('complementary-products-inline__item')) {
				const addButton = button.querySelector('.complementary-products-inline__add-button');
				if (addButton) {
					addButton.classList.value = originalButtonState.classList;
					addButton.innerHTML = originalButtonState.innerHTML;
					addButton.disabled = false;
				}
			}
		}
		
		// Revert accordion state
		if (originalAccordionState !== null && item) {
			const variantsPanel = item.querySelector('.complementary-products-inline__variants');
			if (variantsPanel) {
				variantsPanel.style.display = originalAccordionState;
			}
		}
		
		// Revert plus icon rotation
		if (originalPlusIconState !== null && item) {
			const expandButton = item.querySelector('.complementary-products-inline__add-button--expand');
			if (expandButton) {
				const plusIcon = expandButton.querySelector('.complementary-products-inline__plus-icon');
				if (plusIcon) {
					plusIcon.style.transform = originalPlusIconState;
				}
			}
		}
		
		if (button && button.tagName === 'BUTTON') {
			button.disabled = false;
		}
	});
}

// Handle variant selection changes
document.addEventListener('change', function(e) {
	if (e.target.classList.contains('complementary-products-inline__variant-input')) {
		const item = e.target.closest('.complementary-products-inline__item');
		const variantInputs = item.querySelectorAll('.complementary-products-inline__variant-input:checked');
		const variantsData = item.querySelector('.complementary-products-inline__variants-data');
		
		if (!variantsData) return;

		try {
			const variants = JSON.parse(variantsData.textContent);
			const selectedOptions = Array.from(variantInputs).map(input => input.value);
			
			const selectedVariant = variants.find(variant => {
				return variant.option1 === selectedOptions[0] &&
				       (!selectedOptions[1] || variant.option2 === selectedOptions[1]) &&
				       (!selectedOptions[2] || variant.option3 === selectedOptions[2]);
			});

			if (selectedVariant) {
				item.dataset.variantId = selectedVariant.id;
				console.log('[Complementary Products] Variant updated:', selectedVariant.id);
			}
		} catch (e) {
			console.error('[Complementary Products] Error parsing variants data:', e);
		}
	}
});

// Initialize variant selection on load
document.addEventListener('DOMContentLoaded', function() {
	document.querySelectorAll('.complementary-products-inline__item').forEach(item => {
		if (item.querySelector('.complementary-products-inline__variants')) {
			const variantInputs = item.querySelectorAll('.complementary-products-inline__variant-input:checked');
			const variantsData = item.querySelector('.complementary-products-inline__variants-data');
			
			if (variantsData) {
				try {
					const variants = JSON.parse(variantsData.textContent);
					const selectedOptions = Array.from(variantInputs).map(input => input.value);
					
					const selectedVariant = variants.find(variant => {
						return variant.option1 === selectedOptions[0] &&
						       (!selectedOptions[1] || variant.option2 === selectedOptions[1]) &&
						       (!selectedOptions[2] || variant.option3 === selectedOptions[2]);
					});

					if (selectedVariant) {
						item.dataset.variantId = selectedVariant.id;
					}
				} catch (e) {
					console.error('[Complementary Products] Error parsing variants data:', e);
				}
			}
		}
	});
});

